/**
 * "Add from a case": pick a case, then one of its drafts; the draft's Word
 * file joins the upload list like any other file, so presets, the duplicate
 * check and the client-details scan all apply to it.
 */
import { ArrowLeft, Briefcase, FileText, Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type FC } from "react";
import { toast } from "sonner";

import { DraftsV2API } from "@/api/drafts-v2";
import { DRAFT_TYPE_LABELS, type Draft } from "@/api/types/draft";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { parseCasePath, type CaseRef } from "@/features/caseIde/routes";
import { useGlobalSearch } from "@/features/globalSearch/useGlobalSearch";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (file: File) => void;
};

type PickedCase = { ref: CaseRef; label: string };

const draftLabel = (draft: Draft) =>
  draft.title?.trim() ||
  DRAFT_TYPE_LABELS[draft.draft_type] ||
  String(draft.draft_type);

const fileNameFor = (draft: Draft, served: string) => {
  if (draft.title?.trim()) {
    return `${draft.title.trim().replace(/[\\/:*?"<>|]+/g, " ")}.docx`;
  }
  return served.endsWith(".docx") ? served : `${served}.docx`;
};

export const AddFromCaseDialog: FC<Props> = ({
  open,
  onOpenChange,
  onPick
}) => {
  const search = useGlobalSearch(open);
  const resetRef = useRef(search.reset);
  resetRef.current = search.reset;
  const [picked, setPicked] = useState<PickedCase | null>(null);
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [fetching, setFetching] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    resetRef.current();
    setPicked(null);
    setDrafts(null);
  }, [open]);

  useEffect(() => {
    if (!picked) return undefined;
    let cancelled = false;
    setDrafts(null);
    DraftsV2API.list({ individual_id: picked.ref.individualId })
      .then((rows) => {
        if (!cancelled)
          setDrafts(rows.filter((d) => Boolean(d.docx_storage_path)));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDrafts([]);
        toast.error(
          error instanceof Error ? error.message : "Could not load the drafts."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [picked]);

  const choose = async (draft: Draft) => {
    setFetching(draft.draft_id);
    try {
      const docx = await DraftsV2API.getDocx(draft.draft_id);
      onPick(
        new File([docx.bytes], fileNameFor(draft, docx.filename), {
          type: DOCX_MIME
        })
      );
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not fetch the draft."
      );
    } finally {
      setFetching(null);
    }
  };

  const cases = search.cases
    .map((r) => ({ result: r, ref: parseCasePath(r.to)?.ref ?? null }))
    .filter((c): c is { result: typeof c.result; ref: CaseRef } => !!c.ref);

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add from a case"
      description="Copy a finished draft from a case into the library"
      className="sm:max-w-140"
    >
      {picked ? (
        <Command>
          <div className="flex items-center gap-2 border-b px-3 py-2 text-sm">
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:bg-muted"
              aria-label="Back to cases"
              onClick={() => setPicked(null)}
            >
              <ArrowLeft className="size-4" />
            </button>
            <span className="min-w-0 truncate font-medium">{picked.label}</span>
          </div>
          <CommandInput placeholder="Filter drafts…" />
          <CommandList data-testid="from-case-drafts">
            {drafts === null ? (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                <Spinner className="size-3" /> Loading drafts…
              </div>
            ) : (
              <CommandEmpty>
                This case has no drafts with a document yet.
              </CommandEmpty>
            )}
            {drafts?.length ? (
              <CommandGroup heading="Drafts">
                {drafts.map((draft) => (
                  <CommandItem
                    key={draft.draft_id}
                    value={`${draftLabel(draft)} ${draft.draft_id}`}
                    disabled={fetching !== null}
                    onSelect={() => void choose(draft)}
                    data-testid={`from-case-draft-${draft.draft_id}`}
                  >
                    {fetching === draft.draft_id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <FileText className="size-4 text-muted-foreground" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">
                        {draftLabel(draft)}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[DRAFT_TYPE_LABELS[draft.draft_type], draft.visa_type]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">
            A draft carries this client&apos;s details. The library flags them
            so you can make an anonymized copy before the agents use it.
          </p>
        </Command>
      ) : (
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search cases by client or petitioner…"
            value={search.query}
            onValueChange={search.setQuery}
            data-testid="from-case-input"
          />
          <CommandList>
            {search.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                <Spinner className="size-3" /> Searching…
              </div>
            ) : null}
            {!search.isServerQuery ? (
              <div className="px-3 py-3 text-xs text-muted-foreground">
                Type a few letters of the client&apos;s name.
              </div>
            ) : null}
            {!search.isLoading && search.isServerQuery && !cases.length ? (
              <CommandEmpty>No matching cases.</CommandEmpty>
            ) : null}
            {cases.length ? (
              <CommandGroup heading="Cases">
                {cases.map(({ result, ref }) => (
                  <CommandItem
                    key={`case:${result.id}`}
                    value={`${result.label} ${result.sublabel ?? ""} ${
                      result.id
                    }`}
                    onSelect={() => setPicked({ ref, label: result.label })}
                    data-testid={`from-case-pick-${ref.individualId}`}
                  >
                    <Briefcase className="size-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{result.label}</div>
                      {result.sublabel ? (
                        <div className="truncate text-xs text-muted-foreground">
                          {result.sublabel}
                        </div>
                      ) : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      )}
    </CommandDialog>
  );
};
